import { Typography } from '@healingpaper/cell'
import Link from 'next/link'
import useImpressionTracker from 'react-impression-tracker/src/hooks/useImpressionTracker'

import { useTranslation } from '@/lang'
import { addComma, getDiscountInfo, getPriceWithMonetaryUnit, MonetaryUnitString } from '@/utils/price'
import CheetahIcon, { CheetahIconPack } from '@/components/atomic/atoms/CheetahIcon'
import { getResizedImageUrl, ResizableImageFormats } from '@/utils/image-resizer/imageResizer'
import { useLocales } from '@/shared/hooks/useLocales'

import type { ElementType } from 'react'

interface ServiceOfferCardProps {
  elementId?: string
  eventId: number
  thumbnailUrl: string
  title: string
  subtitle: string
  review?: {
    rating: number
    count: number
  }
  optionId?: string
  price: {
    originalCost: number
    currency: string
    discountedCost?: number
  }
  titleElementType: ElementType
  priceElementType: ElementType
  trackClickEvent?: () => void
  onImpressed?: () => void
}

export const ServiceOfferCard = ({
  elementId,
  eventId,
  thumbnailUrl,
  title,
  subtitle,
  review,
  price,
  optionId,
  titleElementType = 'h3',
  priceElementType = 'span',
  trackClickEvent,
  onImpressed,
}: ServiceOfferCardProps) => {
  const { t } = useTranslation()
  const { locale } = useLocales()
  const { targetRef: impressionElementRef, startTracking } = useImpressionTracker<HTMLAnchorElement, object>({
    context: {},
    onImpressed: () => onImpressed?.(),
    isImpressionVisible: false,
    hasImpressed: false,
    rootMargin: '-66px',
  })

  const textOriginalCost = getPriceWithMonetaryUnit({
    currency: price.currency as MonetaryUnitString,
    price: price.originalCost,
    locale,
  })
  const textFinalCost = getPriceWithMonetaryUnit({
    currency: price.currency as MonetaryUnitString,
    price: price.discountedCost ?? price.originalCost,
    locale,
  })
  const specialPriceText = t('common.price.specialPrice')
  const textDiscountRate = getDiscountInfo({
    originalCost: price.originalCost,
    discountedCost: price.discountedCost || price.originalCost,
    specialPriceText,
  })
  const isDiscounted = price.discountedCost ? price.originalCost > price.discountedCost : false
  const isDiscountVisible = isDiscounted
  const isReviewStaticsVisible = !!review && review.count > 0

  return (
    <Link
      id={elementId}
      className="flex flex-row px-5 py-4 gap-4 bg-white "
      href={`/events/${eventId}${optionId ? `?optionId=${optionId}` : ''}`}
      onClick={trackClickEvent}
      target="_blank"
      ref={impressionElementRef}
    >
      <img
        className="w-[100px] h-[100px] rounded border border-opacity-10 border-black"
        src={getResizedImageUrl(thumbnailUrl, { format: ResizableImageFormats.WEBP, width: 320, height: 320 })}
        alt={title}
        onLoad={() => {
          startTracking()
        }}
      />
      <div className="flex flex-col flex-1">
        <div className="flex flex-col gap-2">
          <div>
            <Typography type="subTitle1" elementType={titleElementType}>
              {title}
            </Typography>
            <Typography type="caption2" color="gray800">
              {subtitle}
            </Typography>
          </div>
          {isReviewStaticsVisible && (
            <div className="flex flex-row items-center gap-[2px]">
              <CheetahIcon name={CheetahIconPack.STAR} width={16} height={16} />
              <Typography type="caption1">{review.rating}</Typography>
              <Typography type="caption1" color="gray700">
                ({addComma(review.count)})
              </Typography>
            </div>
          )}
        </div>
        <div className="flex flex-col self-end items-end">
          {isDiscountVisible && (
            <Typography
              type="caption1"
              color="gray700"
              style={{
                textDecoration: 'line-through',
              }}
            >
              {textOriginalCost}
            </Typography>
          )}
          <div className="flex flex-row items-center gap-1">
            {isDiscountVisible && (
              <Typography type="subTitle0" style={{ color: '#BF8650' }}>
                {textDiscountRate}
              </Typography>
            )}
            <Typography type="title2" elementType={priceElementType} color="gray900">
              {textFinalCost}
            </Typography>
          </div>
        </div>
      </div>
    </Link>
  )
}
